<script>
import { GlobalBus } from '@services/bus'
import { MODAL } from '@constants/Text'
import { filtrarPorTitulo } from '@modules/aawz-intelligence-v2/utils/agrupar-conversas'
import { apiFolders } from '@modules/aawz-intelligence-v2/api/apiFolders'
import {
  ESTADO_INSIGHTS,
  SIDEBAR_CHAT,
} from '@modules/aawz-intelligence-v2/constants/TextChat'
import { rotuloComAtalho } from '@modules/aawz-intelligence-v2/utils/atalho-do-teclado'
import { moverConversaParaPasta } from '@modules/aawz-intelligence-v2/utils/conversation-folder-actions'
import ChatSidebarGroup from '@modules/aawz-intelligence-v2/components/chat-sidebar-group/chat-sidebar-group.vue'
import ChatSidebarGroups from '@modules/aawz-intelligence-v2/components/chat-sidebar-groups/chat-sidebar-groups.vue'
import ChatSidebarRecents from '@modules/aawz-intelligence-v2/components/chat-sidebar-recents/chat-sidebar-recents.vue'
import ChatSidebarEsqueleto from '@modules/aawz-intelligence-v2/components/chat-sidebar-esqueleto/chat-sidebar-esqueleto.vue'
import IaInsightCard from '@modules/aawz-intelligence-v2/components/ia-insight-card/ia-insight-card.vue'
import IaClientePainel from '@modules/aawz-intelligence-v2/components/ia-cliente-painel/ia-cliente-painel.vue'
import { PISO_DE_CARGA_MS } from '@modules/aawz-intelligence-v2/utils/piso-de-carga'
import {
  rolarContainerAte,
  scrollTopParaAlinhar,
} from '@modules/aawz-intelligence-v2/utils/rolagem-do-trilho'
// Créditos são domínio do `ai-framework` (store, copy e a tela de destino moram lá) —
// esta lateral só empresta o rodapé. O componente é autossuficiente: lê a própria fatia
// da store e some sozinho quando o escritório não tem a Central de IA, então nada disso
// sobe para cá.
import SaldoCreditosRodape from '@modules/ai-framework/components/saldo-creditos-rodape/saldo-creditos-rodape.vue'
import AcoesLote from '@components/acoes-lote'
import PersonalizacaoPaleta from '@modules/aawz-intelligence-v2/components/personalizacao-painel/personalizacao-paleta.vue'
import PersonalizacaoCanvas from '@modules/aawz-intelligence-v2/components/personalizacao-painel/personalizacao-canvas.vue'
// Rodapé Cancelar/Restaurar padrão/Salvar — REUSO do mesmo componente do
// editor de personalização do CRM (Painel do Lead/Cliente, Visão Geral).
// "Restaurar padrão" não é gateado por `dirty` (mesma regra de lá): é um
// reset explícito, válido mesmo sem edição local pendente.
import PersonalizacaoFooterActions from '@modules/settings/views/crm/view-crm-personalizacao/personalizacao-footer-actions.vue'

import chatSidebarPersonalizacao from './chat-sidebar-personalizacao'

const DEFAULT_STORE_MODULE = 'aawz-intelligence-v2'
const TAMANHO_ICONE = 14
// O chevron da divisa segue a régua da navbar principal (`sidebar-shell`), não
// a dos botões do topo daqui.
const TAMANHO_ICONE_RETRAIR = 14
const COR_ICONE = 'var(--neutral-700)'
// Agente ativo: ícone na cor da marca do escritório. Medido sobre
// --sidebar-warm-bg: 3.79, acima do mínimo de 3:1 da WCAG 1.4.11 para elemento
// não-textual. O RÓTULO continua neutro — o verde/roxo de marca reprova como
// texto (ver a tabela em `_controls.sass`).
const COR_ICONE_ATIVO = 'var(--primary)'
// Linhas do carregando de Recentes — a anatomia mora em
// `chat-sidebar-esqueleto.vue`, compartilhada com o rodapé da rolagem infinita.
//
// ★ Seis, e não três: o teto de 5 saiu junto com o "Ver todas" e a lista passou
// a mostrar tudo o que está carregado (30, por página). Com três linhas o
// esqueleto ocupava ~100px e a lista entrava com 800 — o salto era mais
// visível que a ausência de transição que ele veio resolver. Seis preenchem a
// dobra, que é o que a pessoa vê enquanto o piso corre.
export const LINHAS_ESQUELETO_INICIAL = 6
// Rodapé da rolagem infinita: TRÊS linhas-fantasma, não uma barra. Uma só
// (14/09/2026, print do usuário) lia como "um item carregando" e, com 30px
// contra os 34 da conversa, entrava e saía com pulo. Três dizem "vem uma
// página" sem tomar a dobra, e a página que chega ocupa exatamente a caixa
// que elas deixam — ver `view-chat.vue` → `carregarMaisConversas`.
export const LINHAS_FANTASMA = 3
// Distância do fim da rolagem que dispara a próxima página. A linha de
// conversa tem 34px na navegação, então 160px é pedir ~5 linhas antes de o
// usuário chegar ao fim: as linhas-fantasma entram abaixo da dobra e ele as
// encontra já acesas ao continuar rolando, em vez de bater no fundo e vê-las
// aparecer. Medido antes de mudar: não é aqui que a rolagem travava (ver o
// rodapé, abaixo).
const MARGEM_DE_CARGA = 160
// Ícone do cabeçalho de Recentes. Só na configuração de HISTÓRICO — ver
// `iconeDaSecaoRecentes`.
const ICONE_SECAO_RECENTES = 'clock'
// Chaves do mapa de seções abertas.
const SECAO_AGENTES = 'agentes'
const SECAO_RECENTES = 'recentes'
const SECAO_AGRUPAR = 'agrupar'
// Estados mutuamente exclusivos da lista, resolvidos no script para o
// template não precisar de condição composta.
const ESTADO_CARREGANDO = 'carregando'
const ESTADO_ERRO = 'erro'
const ESTADO_VAZIO = 'vazio'
const ESTADO_LISTA = 'lista'

/**
 * Sidebar do chat IA redesenhado — "Nova conversa", Agentes, Recentes e
 * "Agrupar por", nesta ordem (AC-5.1).
 *
 * ★ NÃO substitui `conversations.vue`: aquele é o histórico do popup
 * (`chat-ia-pop-up.vue`) e segue vivo e intocado (D-8 do plano). Esta é uma
 * superfície nova, consumida só pela rota `/aawz-intelligence/chat-ia`.
 *
 * Lê o store por `storeModule` — mesmo padrão de `conversations.vue`, porque a
 * Central de IA monta o mesmo motor sob outro namespace.
 */
// Espelha `$duracao-do-recolher` do `<style>` — os dois têm que andar juntos,
// senão a barra de rolagem volta antes de a coluna parar (ou fica calada
// depois). Uma folga de 20ms cobre o quadro de arredondamento.
const DURACAO_DO_RECOLHER_MS = 200

export default {
  name: 'IntelligenceV2ChatSidebar',
  components: {
    ChatSidebarGroup,
    ChatSidebarGroups,
    ChatSidebarRecents,
    ChatSidebarEsqueleto,
    IaInsightCard,
    IaClientePainel,
    SaldoCreditosRodape,
    AcoesLote,
    PersonalizacaoPaleta,
    PersonalizacaoCanvas,
    PersonalizacaoFooterActions,
  },
  // Editor de personalização do painel (rascunho, fade da troca de modo).
  mixins: [chatSidebarPersonalizacao],
  props: {
    storeModule: {
      type: String,
      default: DEFAULT_STORE_MODULE,
    },
    /**
     * O namespace do HISTÓRICO — a lista de conversas e as pastas.
     *
     * ⚠️ [owner, 20/09/2026] *"pq ao clicar em uma aba os itens no recentes
     * mudam ou somem?"*. A lista de conversas e as pastas são do
     * PROFISSIONAL, não da conversa: com as conversas vivas, cada aba tem o
     * próprio módulo Vuex, e ler `storeModule` fazia "Recentes" mostrar a
     * cópia DAQUELA aba — vazia na aba que nunca carregou. As pastas eram pior
     * ainda: `apiFolders.carregar()` sempre escreveu no módulo FIXO (não
     * aceita `storeModule`), então na Home IA elas nunca apareciam.
     *
     * Vazio cai no `storeModule` — popup, painel do Hub e a rota cheia, onde
     * os dois namespaces são o mesmo, seguem byte a byte como antes.
     */
    storeModuleHistorico: {
      type: String,
      default: '',
    },
    // Já filtrado por feature flag pelo pai (`agentesVisiveis($access)`), como
    // em `send-message.vue`: a sidebar não lê `$access` para seguir montável
    // sem sessão no teste.
    agentes: {
      type: Array,
      default: () => [],
    },
    agenteAtivoId: {
      type: String,
      default: '',
    },
    // Eixo de persona que o PAI usou para listar (`view-chat.vue:211`,
    // `contextoListagem`). Os recarregamentos internos da coluna — mover
    // conversa de pasta, apagar pasta — precisam repetir o mesmo eixo, senão o
    // backend aplica o default dele (`persona='CRM'`) e a home encolhe calada.
    // Vazio ⇒ cai na persona ativa do store (`eixoDosRecarregamentos`).
    contextoListagem: {
      type: String,
      default: '',
    },
    // Navegação data-driven do módulo. Nasce VAZIA nesta entrega: mover as
    // abas do módulo para cá ainda é decisão de produto em aberto, e seção sem
    // item é ruído. Sem itens, a seção inteira não renderiza.
    navItems: {
      type: Array,
      default: () => [],
    },
    // Liga a marca do escritório no topo e o gesto de recolher — a lateral
    // passa a fazer o papel de navegação da tela, não só de histórico.
    // Opt-in: a rota `/aawz-intelligence/chat-ia` não passa a prop e continua
    // exatamente como está.
    comoNavegacao: {
      type: Boolean,
      default: false,
    },
    // Página seguinte do histórico em voo. Só desenha o rodapé de carga — quem
    // busca é o pai (é ele que sabe a persona e guarda o `offset`).
    carregandoMais: {
      type: Boolean,
      default: false,
    },
    // `false` quando o backend já devolveu tudo: sem isto o rodapé ficaria
    // pedindo mais para sempre no fim de uma lista completa.
    temMaisConversas: {
      type: Boolean,
      default: false,
    },
    // Linhas de insight já FILTRADAS por permissão e agrupadas pelo pai — a
    // sidebar não lê `$access` nem decide layout, só desenha. Vazio = o bloco
    // inteiro some, então as superfícies de hoje não mudam.
    insights: {
      type: Array,
      default: () => [],
    },
    // Desfecho do painel do escritório, decidido pelo PAI (`ESTADO_INSIGHTS`).
    // `indisponivel` (o endpoint falhou) e `vazio` (respondeu sem indicador)
    // não desenham card nenhum — desenham aviso. Esta lateral não busca nada e
    // não conhece o endpoint, mesmo arranjo de `insights`.
    insightsEstado: {
      type: String,
      default: ESTADO_INSIGHTS.OK,
      validator: (v) => Object.values(ESTADO_INSIGHTS).indexOf(v) !== -1,
    },
    /**
     * Cliente em foco da conversa — quando existe, o painel do ESCRITÓRIO dá
     * lugar ao painel dele.
     *
     * Vem por prop, como `insights`: esta lateral não lê store nem `$access`, e
     * é isso que a mantém montável sem sessão no teste. `null` = conversa geral,
     * e aí nada muda em relação a hoje.
     */
    clienteEmFoco: {
      type: Object,
      default: null,
    },
    // Dados do painel do cliente, JÁ buscados pela view — mesmo arranjo dos
    // insights. `{ ativos, serie, variacao, gruposDeTarefa, carregando }`.
    dadosDoCliente: {
      type: Object,
      default: () => ({}),
    },
    // Atalhos da régua RECOLHIDA: `[{ moduleKey, routeName, icone, rotulo }]`,
    // já filtrados por permissão pelo pai — mesmo arranjo de `insights`, e pelo
    // mesmo motivo (esta sidebar não lê `$access`).
    atalhos: {
      type: Array,
      default: () => [],
    },
    // ⚠️ Gate do botão de busca, decidido pelo PAI. A busca global tem as
    // mesmas condições da topbar (`!deveVerSoFeeFixo && !IS_CLIENTE`), e esta
    // sidebar não lê `$currentUser` nem `$access` de propósito — é o que a
    // mantém montável sem sessão no teste. Mesmo padrão de `insights`.
    buscaGlobalDisponivel: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      TAMANHO_ICONE_RETRAIR,
      LINHAS_ESQUELETO_INICIAL,
      LINHAS_FANTASMA,
      recolhida: false,
      // Largura em movimento — ver `alternarRecolhida`. Só serve para calar a
      // barra de rolagem durante o gesto.
      animandoLargura: false,
      temporizadorDaLargura: null,
      // Ligado na montagem e desligado pelo temporizador — ver
      // `utils/piso-de-carga`. Só a configuração de navegação o usa.
      dentroDoPisoDoEsqueleto: false,
      temporizadorDoEsqueleto: null,
      marcaFalhou: false,
      termoBusca: '',
      // Mutação em voo (mover conversa). Guarda contra duplo-drop e alimenta a
      // barra de progresso entre o drop e o snack.
      movendo: false,
      // Agentes e Recentes abrem por default; "Agrupar por" é a visão
      // alternativa e começa fechada, para não duplicar a lista na tela.
      secoesAbertas: {
        [SECAO_AGENTES]: true,
        [SECAO_RECENTES]: true,
        [SECAO_AGRUPAR]: false,
      },
      // Ids selecionados por ctrl+click nas "Recentes" (só na navegação
      // imersiva — ver `permitirSelecaoLote` em `conversation-row.vue`).
      selectedIds: [],
      // ★ `scrollTop` do último `carregar-mais`. O browser emite vários
      // `scroll` no mesmo ponto (fim de uma rolagem suave, clamp por relayout)
      // — só rolagem REAL além dele pede outra página.
      scrollTopDoUltimoDisparo: 0,
    }
  },
  computed: {
    // Marca do escritório, no topo da coluna — a mesma que a navbar principal
    // mostra, para a imersiva não perder a identidade do white-label.
    //
    // Marca do escritório, no topo da coluna — a mesma que a navbar principal
    // mostra, para a imersiva não perder a identidade do white-label.
    //
    // ⚠️ Vai DIRETO sobre a superfície quente, sem pastilha e sem contorno, por
    // decisão do usuário (26/08) ciente do custo: o asset é cadastrado para a
    // navbar ESCURA, e num escritório cuja logo é clara ela não aparece aqui
    // (medido no 83 — logo e favicon com luminância 255 sobre #F2EFEA, razão
    // ~1:1). Um contorno por `drop-shadow` foi testado e recusado por ficar
    // pesado no tamanho de 22px.
    // O conserto de raiz não é decoração: é ligar `logo_url_branca` /
    // `url_favicon_branca`, hoje COMENTADOS em `src/api/apiUserConfig.js`, e
    // pedir a variante colorida no cadastro do escritório.
    logoEscritorio() {
      return this.$store.getters['parameters/logo']
    },
    // Some ao recolher: a 64px a logo horizontal não cabe, e cortada lê pior
    // que ausente — a mesma regra dos cards de insight logo abaixo.
    marcaVisivel() {
      if (this.recolhida || this.marcaFalhou) return ''
      return this.logoEscritorio
    },
    mostraMarca() {
      return !this.recolhida
    },
    // ⚠️ Os dois estavam no template e NAO existiam no componente — o Vue
    // avisava "Property or method is not defined on the instance" a cada render
    // da lateral, e o efeito era mudo: `alt=""` na logo (fallback de
    // acessibilidade perdido, contra a regra do CLAUDE.md do repo) e a inicial
    // do fallback saindo VAZIA, que e' justamente o caso que ela existe para
    // cobrir — escritorio de logo clara, onde a imagem some no fundo.
    nomeEscritorio() {
      const escritorio = this.$store.getters['parameters/escritorio'] || {}
      return escritorio.nome || ''
    },
    // Uma letra, maiuscula. String vazia (nome ainda nao carregado) devolve ''
    // em vez de quebrar no `[0]`.
    inicialEscritorio() {
      return this.nomeEscritorio.trim().charAt(0).toUpperCase()
    },
    /**
     * ⚠️ Os dois painéis se EXCLUEM. Onde o do cliente aparece, o do escritório
     * some — não são camadas, são o mesmo lugar da coluna respondendo a
     * assuntos diferentes. Com os dois na tela, a pessoa teria o AuC do
     * escritório logo abaixo do patrimônio do cliente, sem nada dizendo qual é
     * qual.
     */
    temPainelDoCliente() {
      return Boolean(!this.recolhida && this.clienteEmFoco)
    },
    temInsights() {
      if (this.temPainelDoCliente) return false
      return !this.recolhida && (this.insights || []).length > 0
    },
    /**
     * ★ O painel do escritório não caiu — ele NÃO CARREGOU, e dizer isso é o
     * que separa estado de dado falso (pendência #266, 03/09/2026). Antes, com
     * `GET /insights` em 404, a coluna preenchia os cards com os números do
     * catálogo do protótipo e um profissional de dois clientes lia "R$ 48,2 M
     * de AuC · 87 clientes" como se fosse do escritório dele.
     */
    mostraInsightsIndisponiveis() {
      if (this.temPainelDoCliente || this.recolhida) return false
      return this.insightsEstado === ESTADO_INSIGHTS.INDISPONIVEL
    },
    // Respondeu, e não há indicador. Sem botão: repetir a chamada devolveria o
    // mesmo nada.
    mostraInsightsVazio() {
      if (this.temPainelDoCliente || this.recolhida) return false
      return this.insightsEstado === ESTADO_INSIGHTS.VAZIO
    },
    /**
     * A régua recolhida: os mesmos módulos do painel, virados em ícone.
     *
     * ★ Espelha a lateral aberta. Recolhida ela ficava com dois botões e um
     * trilho vazio de 64px por toda a altura da tela — quem recolhia perdia o
     * acesso aos módulos e tinha de reabrir para clicar em qualquer um.
     *
     * O inverso de `temInsights`, de propósito: onde um aparece o outro some, e
     * é isso que faz os dois serem a MESMA informação em dois tamanhos.
     */
    temAtalhos() {
      return (
        this.recolhida &&
        this.comoNavegacao &&
        (this.atalhos || []).length > 0
      )
    },
    iconeRecolher() {
      return this.recolhida ? 'mdi-chevron-right' : 'mdi-chevron-left'
    },
    tituloRecolher() {
      return this.recolhida ? 'Expandir menu' : 'Recolher menu'
    },

    texto() {
      return SIDEBAR_CHAT
    },
    // Tooltip com o atalho na notação da plataforma (⌘ no Mac, Ctrl no resto).
    rotuloNovaConversa() {
      return rotuloComAtalho(SIDEBAR_CHAT.novaConversa, SIDEBAR_CHAT.teclasNovaConversa)
    },
    rotuloBuscaGlobal() {
      return rotuloComAtalho(SIDEBAR_CHAT.buscaGlobal, SIDEBAR_CHAT.teclasBuscaGlobal)
    },
    // Uma ação só, destrutiva — mesmo formato de `acoes` que
    // `curadoria/triagem-lista.vue` já passa pra este mesmo componente.
    acoesLote() {
      return [
        {
          icon: 'trash',
          title: this.texto.removerConversa,
          action: this.confirmDeleteLote,
          danger: true,
        },
      ]
    },
    secoes() {
      return {
        agentes: SECAO_AGENTES,
        recentes: SECAO_RECENTES,
        agrupar: SECAO_AGRUPAR,
      }
    },
    tamanhoIcone() {
      return TAMANHO_ICONE
    },
    corIcone() {
      return COR_ICONE
    },
    /** O namespace de onde o HISTÓRICO é lido — ver a prop `storeModuleHistorico`. */
    nsDoHistorico() {
      return this.storeModuleHistorico || this.storeModule
    },
    conversas() {
      return this.$store.getters[this.nsDoHistorico + '/conversations'] || []
    },
    pastas() {
      return this.$store.getters[this.nsDoHistorico + '/folders'] || []
    },
    carregando() {
      return Boolean(
        this.$store.getters[this.nsDoHistorico + '/conversationsLoading'] ||
          this.$store.getters[this.nsDoHistorico + '/foldersLoading']
      )
    },
    erro() {
      return Boolean(
        this.$store.getters[this.nsDoHistorico + '/conversationsError']
      )
    },
    selectedId() {
      const sel =
        this.$store.getters[this.storeModule + '/selectedConversation']
      return sel && sel.id ? sel.id : null
    },
    // Busca client-side por `topic` sobre o que já está carregado: o
    // GET /conversations não tem parâmetro de busca (gap G8).
    // `filtrarPorTitulo` (do #1961) em vez do filtro inline que estava aqui: ele
    // normaliza acento antes de comparar (NFD + strip de combining marks), então
    // "captacao" encontra "Captação". O nosso só fazia `toLowerCase`.
    conversasFiltradas() {
      return filtrarPorTitulo(this.conversas, this.termoBusca)
    },
    // O erro só toma a tela quando não há lista para mostrar: falhar um
    // refresh não pode apagar o que o usuário já estava vendo.
    estadoLista() {
      // ⚠️ O piso vem ANTES de `conversas.length`, e é o que o faz existir: com
      // o store já preenchido, a lista vence sempre e o esqueleto nunca
      // apareceria — que é exatamente o caso na `/ia`.
      if (this.dentroDoPisoDoEsqueleto) return ESTADO_CARREGANDO
      if (this.conversas.length) return ESTADO_LISTA
      if (this.carregando) return ESTADO_CARREGANDO
      if (this.erro) return ESTADO_ERRO
      return ESTADO_VAZIO
    },
    mostraCarregando() {
      return this.estadoLista === ESTADO_CARREGANDO
    },
    mostraErro() {
      return this.estadoLista === ESTADO_ERRO
    },
    mostraVazio() {
      return this.estadoLista === ESTADO_VAZIO
    },
    // Busca sem resultado tem saída (limpar o termo) e por isso é um estado
    // distinto de "não há conversa nenhuma".
    buscaSemResultado() {
      return (
        this.estadoLista === ESTADO_LISTA && !this.conversasFiltradas.length
      )
    },
    // Só na imersiva: na rota cheia o campo de busca continua ali, e dois
    // caminhos para a mesma coisa na mesma coluna é ruído.
    mostraBuscaGlobal() {
      return this.comoNavegacao && this.buscaGlobalDisponivel
    },
    itensAgentes() {
      const ativoId = this.agenteAtivoId
      return this.agentes.map(function (agente) {
        const ativo = agente.id === ativoId
        return {
          id: agente.id,
          label: agente.label,
          iconName: agente.iconName,
          ativo: ativo,
          corIcone: ativo ? COR_ICONE_ATIVO : COR_ICONE,
        }
      })
    },
    totalCarregado() {
      return this.conversasFiltradas.length
    },
    // Na navegação o cabeçalho vira só a etiqueta da lista: relógio e contagem
    // eram chrome num bloco que já é o último da coluna e cujo conteúdo está
    // logo abaixo. `''` esconde o ícone (`temIcone`) e `null` a contagem
    // (`mostraContador`) — zero NÃO esconde, e aqui não queremos um "0".
    iconeDaSecaoRecentes() {
      if (this.comoNavegacao) return ''
      return ICONE_SECAO_RECENTES
    },
    contadorDeRecentes() {
      if (this.comoNavegacao) return null
      return this.totalCarregado
    },
    // Só com a lista já na tela: durante a carga INICIAL quem fala é o
    // esqueleto da seção, e os dois juntos seriam dois estados de carga
    // empilhados dizendo a mesma coisa.
    mostraCargaDeMais() {
      return this.carregandoMais && this.estadoLista === ESTADO_LISTA
    },
    temNavItems() {
      return this.navItems.length > 0
    },
    temAgentes() {
      return this.agentes.length > 0
    },
    personaAtiva() {
      return this.$store.getters[this.storeModule + '/persona']
    },
    // Eixo dos recarregamentos internos. Hoje o único pai (`view-chat.vue`,
    // `contextoListagem`) sempre manda um eixo; a queda na persona ativa é
    // rede de segurança para um host que monte a coluna sem a prop — sem
    // eixo o backend cai em CRM.
    eixoDosRecarregamentos() {
      return this.contextoListagem || this.personaAtiva || undefined
    },
    /**
     * O painel de artefato está aberto (Fase 2b, plano
     * `moonlit-conjuring-sun`). O getter existe em qualquer superfície que
     * componha `store/artefato.js` (o namespace `aawz-intelligence-v2` inteiro),
     * mas só vira `true` na `/home-ia`: é lá que `modoAtivo` fica ligado
     * (`view-chat-imersivo.vue::mounted`), o único lugar onde
     * `chat-message.vue` monta o card de prévia que despacha `abrirArtefato`.
     * Nas outras rotas (`chat-ia` de página cheia, popup) o watch abaixo
     * nunca dispara — ninguém ali abre o artefato.
     */
    artefatoAberto() {
      return Boolean(this.$store.getters[this.storeModule + '/artefatoAberto'])
    },
  },
  watch: {
    // A lista recarregada do zero (troca de eixo, retry) solta a trava do
    // último disparo: a lista nova pode ser mais curta, e um `scrollTop`
    // guardado da anterior seguraria o gatilho para sempre.
    carregando(agora) {
      if (agora) this.scrollTopDoUltimoDisparo = 0
    },
    // A invalidação por persona existe em `apiFolders.js:52` e, sem este watch,
    // NUNCA era disparada: o `carregar` só rodava no `created`, e a troca de
    // agente (`view-chat.irParaAgente`) não recria a sidebar — ela não tem
    // `:key`. O cache seguia com o universo do agente anterior, que é
    // exatamente o sintoma que o comentário do `created` diz prevenir.
    personaAtiva(nova) {
      if (nova) this.carregarPastas()
    },
    /**
     * Recolhe a lateral ao abrir o artefato (Fase 2b) — igual ao canvas do
     * ChatGPT, ela NÃO reabre sozinha quando o artefato fecha (decisão
     * confirmada no plano). Só a transição `false → true` importa: por isso o
     * `if` cobre só a abertura, nunca força `false` de volta.
     */
    artefatoAberto(aberto) {
      if (aberto) this.recolhida = true
    },
  },
  mounted() {
    // Piso do esqueleto — só na navegação. Na rota cheia e no popup a lista
    // continua aparecendo assim que houver dado.
    if (!this.comoNavegacao) return
    this.dentroDoPisoDoEsqueleto = true
    this.temporizadorDoEsqueleto = setTimeout(() => {
      this.dentroDoPisoDoEsqueleto = false
    }, PISO_DE_CARGA_MS)
  },
  beforeDestroy() {
    // Timer órfão sobrevive à troca de rota e escreve num componente destruído
    // — regra do `CLAUDE.md` do repo.
    if (this.temporizadorDoEsqueleto) clearTimeout(this.temporizadorDoEsqueleto)
    if (this.temporizadorDaLargura) clearTimeout(this.temporizadorDaLargura)
  },
  created() {
    // As pastas do profissional (setter puro na store compartilhada). As
    // CONVERSAS são carregadas pelo componente-pai, que é quem sabe a persona
    // ativa — mesma divisão de `conversations.vue`. Sem esta linha o modo
    // "Pastas" nasceria vazio na rota que não abre o popup.
    // `catch` obrigatório: chamada disparada em hook de ciclo de vida, sem
    // ninguém para receber a rejeição. As pastas são acessório — sem elas a
    // sidebar ainda lista as conversas —, então falhar aqui degrada o modo
    // "Pastas" e não derruba a tela (nem vaza unhandled rejection).
    // `persona` junto: o contador de cada pasta tem que olhar o mesmo universo
    // que a lista de conversas ao lado, senão o chip conta conversas de outro
    // agente e a pasta abre vazia.
    this.carregarPastas()
  },
  methods: {
    // Rolagem infinita do histórico: perto do fim, pede a próxima página ao pai.
    //
    // ⚠️ O evento é do `__corpo`, não da janela — na navegação quem rola é ele
    // (a casca não pode ter `overflow`, senão o botão da divisa é recortado).
    // Só na navegação: na rota cheia a lista tem teto e o botão "Ver todas".
    //
    // ★ A posição de rolagem do usuário NUNCA é alterada por código neste
    // fluxo (padrão clássico, ChatGPT/Twitter — 14/09/2026, depois de uma
    // rolagem automática rejeitada ao vivo: "não chega ao fim, volta, depois
    // chega"). O esqueleto é o fim rolável do conteúdo; a página entra no
    // lugar dele e a lista só cresce PARA BAIXO.
    //
    // ⚠️ Além de `carregandoMais`/`temMaisConversas`, só rolagem REAL além do
    // último disparo pede página (`scrollTopDoUltimoDisparo`): o browser
    // emite vários `scroll` no mesmo ponto, e cada um deles pediria a mesma
    // página de novo.
    aoRolar(evento) {
      if (!this.comoNavegacao) return
      if (this.carregandoMais || !this.temMaisConversas) return
      const el = evento.target
      if (el.scrollTop <= this.scrollTopDoUltimoDisparo) return
      const restante = el.scrollHeight - el.scrollTop - el.clientHeight
      if (restante > MARGEM_DE_CARGA) return
      this.scrollTopDoUltimoDisparo = el.scrollTop
      this.$emit('carregar-mais')
    },
    // O palette vive em `global-modals.vue`, fora desta árvore — o bus é o
    // contrato do repo para isso.
    //
    // `IA_PALETTE` porque este botão só aparece na imersiva (`mostraBuscaGlobal`),
    // e lá quem responde é o palette da IA: é ele que tem as conversas com
    // agrupamento por pasta, que era justamente o que a lateral perdeu aqui.
    abrirBuscaGlobal() {
      GlobalBus.$emit(MODAL.IA_PALETTE)
    },
    /**
     * Recolhe/expande a coluna.
     *
     * ⚠️ A marca de "animando" é ligada NO CLIQUE, antes de a largura mudar. O
     * `__corpo` rola (`overflow-y: auto`), e enquanto a coluna estreita o
     * conteúdo sobra por um instante — o browser desenha a barra de rolagem
     * nesse meio-tempo e ela pisca no fim do gesto. Calada durante o
     * movimento, o gesto fica limpo; depois ela volta, porque a lista de
     * conversas continua rolando.
     */
    alternarRecolhida() {
      this.recolhida = !this.recolhida
      this.animandoLargura = true
      // Timer órfão sobrevive à troca de rota — limpo no `beforeDestroy`, e
      // substituído aqui se o gesto for repetido antes de terminar.
      if (this.temporizadorDaLargura) clearTimeout(this.temporizadorDaLargura)
      this.temporizadorDaLargura = setTimeout(() => {
        this.animandoLargura = false
      }, DURACAO_DO_RECOLHER_MS)
    },
    // `matchMedia` pode não existir (jsdom antigo): sem ele, cai no suave, que
    // é o comportamento da maioria.
    comportamentoDaRolagem() {
      const mq = window.matchMedia
      if (!mq) return 'smooth'
      return mq('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    },
    /**
     * Botão do relógio na régua recolhida: abre a coluna E leva até "Recentes".
     *
     * Só expandir deixava a pessoa no topo, olhando o painel de situação, com a
     * lista que ela pediu fora da dobra — o gesto prometia histórico e entregava
     * a lateral inteira.
     *
     * ⚠️ Rola no `$nextTick`: a seção só existe no DOM depois do re-render que
     * a expansão dispara (recolhida, o corpo mostra a régua no lugar dela).
     * Medir antes acha `undefined`.
     *
     * ⚠️ Garante a seção ABERTA antes de rolar. Ela abre por padrão, mas quem
     * a fechou numa sessão anterior chegaria só no cabeçalho — e o botão teria
     * levado a um lugar sem a lista que ele nomeia.
     */
    abrirHistorico() {
      this.recolhida = false
      this.$set(this.secoesAbertas, SECAO_RECENTES, true)
      this.$nextTick(() => {
        const secao = this.$refs.secaoRecentes
        const alvo = secao && secao.$el
        const corpo = this.$refs.corpo
        if (!alvo || !corpo) return
        // Só o `__corpo` rola (a conta é a do trilho, `rolagem-do-trilho.js`):
        // `scrollIntoView` subiria a cadeia inteira de ancestrais roláveis —
        // na imersiva eles têm `overflow: hidden`, que rola por programação.
        // Suave só para quem não pediu o contrário — a rolagem automática é
        // exatamente o tipo de movimento que `prefers-reduced-motion` cobre.
        rolarContainerAte(corpo, scrollTopParaAlinhar(corpo, alvo), {
          suave: this.comportamentoDaRolagem() === 'smooth',
        })
      })
    },
    aoFalharMarca() {
      this.marcaFalhou = true
    },

    carregarPastas() {
      // `APITemplate` devolve envelope (nunca rejeita) — o que chega aqui é
      // exceção de programação, e ela não pode sumir em silêncio.
      apiFolders
        .carregar({ persona: this.personaAtiva })
        .catch((erro) => console.warn('[IA] pastas não carregaram: ' + (erro && erro.message)))
    },
    alternarSecao(chave) {
      this.$set(this.secoesAbertas, chave, !this.secoesAbertas[chave])
    },
    // `clearable` do campo emite `null` ao limpar; o filtro trabalha com string.
    onBusca(valor) {
      this.termoBusca = valor || ''
    },
    onSelect(conversa) {
      this.$emit('select', conversa)
    },
    onRename(payload) {
      this.$emit('rename', payload)
    },
    confirmDelete(conversa) {
      this.$showModal({
        type: 'danger',
        v2: true,
        center: true,
        title: this.texto.removerConversaTitulo,
        text: this.texto.removerConversaTexto,
        buttons: [
          { text: this.texto.cancelar },
          {
            text: this.texto.removerConversa,
            action: () => this.$emit('delete', conversa),
          },
        ],
      })
    },
    // Ctrl+click nas "Recentes" — ver `permitirSelecaoLote`/`toggle-select`
    // em `conversation-row.vue`.
    onToggleSelect(id) {
      const indice = this.selectedIds.indexOf(id)
      if (indice === -1) {
        this.selectedIds.push(id)
      } else {
        this.selectedIds.splice(indice, 1)
      }
    },
    limparSelecaoLote() {
      this.selectedIds = []
    },
    // Mesmo formato de `confirmDelete` acima, pluralizado pela contagem. O
    // backend só tem exclusão unitária (`PATCH /conversations/<id>`) — quem
    // faz o loop sequencial é `deletarConversasEmLote` em `view-chat.vue`
    // (mesmo padrão de `view-curadoria.vue::descartarEmLote`).
    confirmDeleteLote() {
      const ids = this.selectedIds.slice()
      this.$showModal({
        type: 'danger',
        v2: true,
        center: true,
        title: this.texto.removerConversasLoteTitulo,
        text: this.texto.removerConversasLoteTexto(ids.length),
        buttons: [
          { text: this.texto.cancelar },
          {
            text: this.texto.removerConversa,
            action: () => {
              this.selectedIds = []
              this.$emit('delete-lote', ids)
            },
          },
        ],
      })
    },
    // Guard de reentrância: sem ele, dois drops seguidos disparam dois PATCH e
    // dois recarregamentos concorrentes, e o último a responder vence.
    async moverConversa(payload) {
      if (this.movendo) return
      this.movendo = true
      try {
        const r = await moverConversaParaPasta({
          ...payload,
          context: this.eixoDosRecarregamentos,
        })
        // `moverConversaParaPasta` devolve `null` quando falta `conversationId`
        // — snack sem texto ficaria 8s na tela dizendo nada.
        if (r) this.$showSnack(r)
      } finally {
        this.movendo = false
      }
    },
  },
}
</script>

<template>
  <!-- <aside> é o landmark nativo (complementary). ⚠️ A folha global da
       nav-bar (`src/components/nav-bar.vue`, bloco `scope` sem o "d", logo
       global) declara `aside { position: fixed !important }`, o que tiraria
       esta coluna do fluxo: ela deixaria de acompanhar a altura do chat e
       cresceria pra fora da tela ao expandir as seções (verificado no browser).
       Por isso `.chat-sidebar` fixa `position: relative !important` no
       <style>: entre dois `!important` vence a especificidade, e classe +
       atributo do scoped ganham de um seletor de tag. -->
  <aside
    class="chat-sidebar"
    :class="{
      'chat-sidebar--recolhida': recolhida,
      'chat-sidebar--animando': animandoLargura,
      'chat-sidebar--navegacao': comoNavegacao,
      'chat-sidebar--trocando-modo': trocandoModo,
    }"
  >
    <!-- Barra do topo: a marca do escritório à esquerda e, à direita, as duas
         ações que não dependem de ler texto — buscar e começar de novo. Mesma
         anatomia da lateral do Decade, e a mesma da navbar principal, onde a
         logo também abre a coluna.
         O gesto de RECOLHER não está aqui: virou o botão da divisa, logo abaixo
         deste bloco. Recolhida, a barra some inteira e sobra só aquele botão. -->
    <div v-if="comoNavegacao" class="chat-sidebar__barra">
      <div v-if="mostraMarca" class="chat-sidebar__marca">
        <img
          v-if="marcaVisivel"
          class="chat-sidebar__marca-img"
          :src="marcaVisivel"
          :alt="nomeEscritorio"
          @error="aoFalharMarca"
        />
        <!-- Sem imagem (ou com imagem que falhou) sobra a inicial: ela sempre
             aparece, independente do arquivo cadastrado. -->
        <span v-else class="chat-sidebar__marca-inicial ia-texto-micro">
          {{ inicialEscritorio }}
        </span>
      </div>

      <div class="chat-sidebar__acoes-topo">
        <button
          v-if="mostraBuscaGlobal"
          type="button"
          class="chat-sidebar__botao-topo"
          data-test="busca-global-topo"
          :title="rotuloBuscaGlobal"
          :aria-label="rotuloBuscaGlobal"
          @click="abrirBuscaGlobal"
        >
          <icon-svg name="search" :size="tamanhoIcone" :color="corIcone" />
        </button>
        <!-- ★ Entre a lupa e o "+" — trava atrás da prop
             `personalizacaoDisponivel` (kill-switch de `constants/Feature.js`
             + guard do backend, decididos pela view imersiva). Mesmo ícone/tooltip de
             `lead-summary-card.vue` (scope profissional): `tune` +
             "Personalizar meu painel", nada de ícone novo.
             ⚠️ Tooltip NATIVO (`title`), como os outros dois botões desta
             fileira — não `v-tooltip`: ele embrulha o ativador num wrapper
             0×0 que AINDA conta como item de flex, e o `gap` do container é
             somado antes E depois dele, dobrando o respiro ao redor do
             botão (mesma armadilha do `EscopoSelect`/`+` do composer, já
             documentada no CLAUDE.md deste módulo) — medido ao vivo aqui. -->
        <button
          v-if="personalizacaoDisponivel"
          type="button"
          class="chat-sidebar__botao-topo"
          data-test="personalizar-painel-topo"
          :title="texto.personalizarPainel"
          :aria-label="texto.personalizarPainel"
          @click="alternarPersonalizacaoComFade"
        >
          <icon-svg name="tune" :size="tamanhoIcone" :color="corIcone" />
        </button>
        <!-- Recolhida, este botão passa à FRENTE da lupa por `order` no CSS
             (ver `.chat-sidebar--recolhida`), sem mexer no DOM: expandida, a
             ordem lupa → personalizar → "+" é a de sempre. -->
        <button
          type="button"
          class="chat-sidebar__botao-topo chat-sidebar__botao-topo--nova"
          data-test="nova-conversa-topo"
          :title="rotuloNovaConversa"
          :aria-label="rotuloNovaConversa"
          @click="$emit('new-conversation')"
        >
          <!-- `add-circle` e não o `edit` do protótipo: é o ícone que ESTA
               ação já tinha na linha "Nova conversa" desta mesma lateral, e o
               gesto não mudou — só o lugar do botão. Trocar o glifo junto com o
               lugar faria o usuário procurar duas vezes. -->
          <icon-svg name="add-circle" :size="tamanhoIcone" :color="corIcone" />
        </button>
      </div>
    </div>

    <!-- ★ "Adicionar módulo" do editor de personalização — logo abaixo da
         barra do topo, IRMÃ do `__corpo` (nunca dentro dele): fora da área
         que rola, numa posição ESTÁVEL, que não se desloca conforme itens
         entram/saem do canvas. Faixa de fundo mais forte que a lateral, de
         borda a borda, pra ler como "de onde os módulos vêm" (referência do
         usuário). -->
    <PersonalizacaoPaleta
      v-if="modoPersonalizacao && !recolhida"
      class="chat-sidebar__personalizacao-paleta"
      :itens="personalizacaoDraftOcultos"
      @adicionar="aoAdicionarAoRascunho"
    />

    <!-- ★ O que ROLA vive aqui dentro, e não na casca. A casca precisa ficar
         sem `overflow` para o botão da divisa poder vazar pela borda direita:
         um eixo não-`visible` força o outro a `auto` (CSS Overflow §3), então
         com `overflow-y: auto` na casca o `right: -13px` seria recortado.
         Mesma anatomia do `sidebar-shell` da navbar principal, onde o toggle é
         IRMÃO do corpo rolável, justamente para não ser cortado por ele. -->
    <div
      ref="corpo"
      class="chat-sidebar__corpo"
      :class="{ 'chat-sidebar__corpo--personalizando': modoPersonalizacao }"
      @scroll="aoRolar"
    >
      <!-- Régua recolhida: um botão por módulo do painel, mais o histórico.
           Ver `temAtalhos`. -->
      <nav v-if="temAtalhos" class="chat-sidebar__atalhos">
        <!-- Divisa de ENTRADA: separa os módulos das ações da barra do topo
             (personalizar, buscar, nova conversa) — mesma régua que fecha o
             bloco antes do histórico (pedido do usuário, 17/09). -->
        <span class="chat-sidebar__atalhos-divisa" aria-hidden="true" />

        <button
          v-for="atalho in atalhos"
          :key="atalho.moduleKey"
          type="button"
          class="chat-sidebar__botao-topo"
          :title="atalho.rotulo"
          :aria-label="atalho.rotulo"
          @click="$emit('abrir-insight', atalho)"
        >
          <icon-svg
            :name="atalho.icone"
            :size="tamanhoIcone"
            :color="corIcone"
          />
        </button>

        <!-- Divisa como ELEMENTO, e não como borda do botão: o histórico tem
             de ter a mesma caixa dos módulos, e uma borda nele o obrigava a
             esticar para a linha ficar visível. -->
        <span class="chat-sidebar__atalhos-divisa" aria-hidden="true" />

        <!-- Histórico: o relógio da seção "Recentes", que é onde este botão
             leva. Ele ABRE a lateral em vez de navegar — a lista de conversas
             não cabe em 64px, e o destino é a própria coluna expandida. -->
        <button
          type="button"
          class="chat-sidebar__botao-topo"
          :title="texto.verHistorico"
          :aria-label="texto.verHistorico"
          @click="abrirHistorico"
        >
          <icon-svg
            name="clock"
            :size="tamanhoIcone"
            :color="corIcone"
          />
        </button>
      </nav>

      <!-- Navegação data-driven. Sem itens, a seção inteira some. -->
      <nav v-if="temNavItems" class="chat-sidebar__nav">
        <button
          v-for="item in navItems"
          :key="item.id"
          type="button"
          class="ia-sidebar-row"
          :title="item.label"
          @click="$emit('select-nav', item.id)"
        >
          <icon-svg
            :name="item.iconName"
            :size="tamanhoIcone"
            :color="corIcone"
            class="flex-shrink-0"
          />
          <span class="text-truncate">{{ item.label }}</span>
        </button>
      </nav>

      <!-- Painel de situação do escritório. Antes da busca, como no protótipo:
         o gestor abre a IA e já vê onde olhar, antes de procurar conversa.
         Sai inteiro quando a lateral está recolhida — número de 26px não cabe
         em 64px, e card cortado é pior que card ausente. -->
      <!-- ★ Painel do CLIENTE no lugar do painel de módulos. `out-in` é
           obrigatório: sem ele os dois coexistem por um quadro e a coluna dobra
           de altura no meio da troca. O nome `fade` segue a receita do DS
           (`--motion-duration-default` + `--motion-easing-standard`) — uma
           transição só para a `/ia`. -->
      <!-- ⚠️ [bug corrigido, 31/08] `v-if` com `!recolhida` na `<transition>`
           NÃO bloqueia o leave: o Vue intercepta a remoção do NÓ REAL sempre
           que ele sai do DOM, mesmo quando quem mudou foi um `v-if` de fora —
           não só o `key` de dentro. Medido ao vivo: ao recolher,
           `.chat-sidebar__insights` ficava no DOM com `fade-leave-active`
           pela duração INTEIRA da transição de largura (~250ms), contribuindo
           ~750px de altura pro `.chat-sidebar__corpo` — o scroll vertical que
           só sumia quando a régua terminava de fechar. `v-show` no wrapper
           NUNCA remove o nó (só alterna `display`), então nunca dispara leave
           nenhum — some no mesmo quadro, igual ao `.chat-sidebar__secoes`
           (`display: none` puro) alguns metros abaixo. A troca painel↔módulos
           continua com fade normalmente (as duas acontecem com a coluna
           aberta, sem o wrapper interferir) — quem perde o fade é só o
           recolher, que não deveria ter um mesmo. -->
      <div
        v-show="!recolhida"
        :class="{ 'chat-sidebar__painel-superior--personalizando': modoPersonalizacao }"
      >
      <!-- Enquanto o corpo está esmaecido pela troca de modo
           (`--trocando-modo`), este `fade` interno é DESLIGADO por CSS
           (`transition: none` nas classes `.fade-*`): senão o canvas ainda
           estaria entrando quando o corpo voltasse — um segundo fade por
           cima do primeiro. Por CSS e não trocando o `name`: mudar o `name`
           re-renderizava a `<transition>` e o conteúdo novo era inserido e
           removido no mesmo ciclo (medido nos frames do screencast do
           usuário, 16/09/2026). -->
      <transition
        name="fade"
        mode="out-in"
        @after-enter="aoEntrarPainelNovo"
      >
        <div
          v-if="modoPersonalizacao"
          key="personalizacao"
          class="chat-sidebar__insights chat-sidebar__personalizacao"
          data-test="personalizacao-painel"
        >
          <PersonalizacaoCanvas
            :itens="personalizacaoDraftVisiveis"
            @reordenar="aoReordenarRascunho"
            @remover="aoOcultarNoRascunho"
          />
        </div>

        <div
          v-else-if="temPainelDoCliente"
          key="cliente"
          class="chat-sidebar__insights chat-sidebar__insights--cliente"
        >
          <IaClientePainel
            :cliente="clienteEmFoco"
            :ativos="dadosDoCliente.ativos"
            :serie="dadosDoCliente.serie"
            :variacao="dadosDoCliente.variacao"
            :grupos-de-tarefa="dadosDoCliente.gruposDeTarefa"
            :modo="dadosDoCliente.modo"
            :timeline="dadosDoCliente.timeline"
            :contratos="dadosDoCliente.contratos"
            :transcricoes="dadosDoCliente.transcricoes"
            :carregando="dadosDoCliente.carregando"
            @abrir-perfil="$emit('abrir-perfil-cliente', $event)"
            @abrir-tarefas="$emit('abrir-tarefas-cliente', $event)"
            @abrir-contatos="$emit('abrir-contatos-cliente', $event)"
            @abrir-carteira="$emit('abrir-carteira-cliente', $event)"
            @abrir-contratos="$emit('abrir-contratos-cliente', $event)"
            @abrir-transcricoes="$emit('abrir-transcricoes-cliente', $event)"
          />
        </div>

        <div
          v-else-if="mostraInsightsIndisponiveis"
          key="insights-indisponiveis"
          class="chat-sidebar__insights chat-sidebar__aviso chat-sidebar__aviso--linha"
          data-test="insights-indisponiveis"
        >
          <div class="chat-sidebar__aviso-texto">
            <div class="ia-sidebar-aux">{{ texto.insightsIndisponiveis }}</div>
            <div class="ia-sidebar-aux">
              {{ texto.insightsIndisponiveisAjuda }}
            </div>
          </div>
          <!-- [feedback do usuário, 05/09] "mova o baseButton para um vbtn
               normal mantendo o icone, e coloque no lado direito e nao
               abaixo do texto" / "so quero icone" / "sem cor primary, deixa
               como tava" — só o ícone, ao lado do aviso (não embaixo), sem
               cor de marca. -->
          <v-btn
            icon
            small
            class="chat-sidebar__aviso-retry"
            :aria-label="texto.tentarDeNovo"
            :title="texto.tentarDeNovo"
            @click="$emit('recarregar-insights')"
          >
            <icon-svg name="refresh" :size="14" color="var(--neutral-700)" />
          </v-btn>
        </div>

        <div
          v-else-if="mostraInsightsVazio"
          key="insights-vazio"
          class="chat-sidebar__insights chat-sidebar__aviso ia-sidebar-aux"
          data-test="insights-vazio"
        >
          {{ texto.insightsVazio }}
        </div>

        <div v-else-if="temInsights" key="modulos" class="chat-sidebar__insights">
          <!-- `--colunas` vem da LINHA, não de uma classe por número: quantas
             colunas ela tem é dado do catálogo (`porLinha`), e uma classe por
             valor obrigaria a inventar CSS novo a cada bloco que surgisse. -->
        <div
          v-for="linha in insights"
          :key="linha.chave"
          class="chat-sidebar__insight-linha"
          :class="{
            'chat-sidebar__insight-linha--grade': linha.itens.length > 1,
            'chat-sidebar__insight-linha--compacta': linha.compacta,
          }"
          :style="{ '--colunas': linha.colunas }"
        >
          <IaInsightCard
            v-for="item in linha.itens"
            :key="item.id"
            v-bind="item"
            @abrir="$emit('abrir-insight', item)"
          />
        </div>
        </div>
      </transition>
      </div>

      <!-- Na imersiva ela virou o botão do topo; aqui continua a linha de sempre,
         que é o que a rota cheia do Chat IA usa. -->
      <button
        v-if="!comoNavegacao"
        type="button"
        class="ia-sidebar-row chat-sidebar__nova"
        :title="rotuloNovaConversa"
        @click="$emit('new-conversation')"
      >
        <icon-svg
          name="add-circle"
          :size="tamanhoIcone"
          :color="corIcone"
          class="flex-shrink-0"
        />
        <span>{{ texto.novaConversa }}</span>
      </button>

      <!-- ⚠️ Campo e lupa compacta são da ROTA CHEIA. Na imersiva a busca de
         conversa mudou de casa: vive no Ctrl/Cmd+K, junto com agentes e
         módulos, e o gatilho é o botão do topo. Mantendo os dois aqui sob o
         gate, `/aawz-intelligence/chat-ia` fica byte-idêntica — e `termoBusca`,
         `conversasFiltradas` e `buscaSemResultado` seguem servindo ela, sem
         virar código morto. -->
      <template v-if="!comoNavegacao">
        <!-- Recolhida, o campo não cabe — mas sumir sem deixar nada tornava o
           trilho inútil. Vira um atalho que expande a lateral. -->
        <button
          v-if="recolhida"
          type="button"
          class="ia-sidebar-row chat-sidebar__buscar-compacta"
          :title="texto.buscaPlaceholder"
          :aria-label="texto.buscaPlaceholder"
          @click="alternarRecolhida"
        >
          <icon-svg name="search" :size="tamanhoIcone" :color="corIcone" />
        </button>

        <input-by-type
          v-else
          class="ia-sidebar-busca my-2 mx-2"
          type="search"
          max-width="100%"
          clearable
          :value="termoBusca"
          :placeholder="texto.buscaPlaceholder"
          :aria-label="texto.buscaPlaceholder"
          @input="onBusca"
        />
      </template>

      <!-- 2px entre o drop e o snack: mover conversa recarrega as duas listas e
         até aqui não dava sinal nenhum de que algo estava acontecendo. -->
      <v-progress-linear
        v-if="movendo"
        class="ia-sidebar-progresso"
        indeterminate
        height="2"
      />

      <!-- Editor de personalização aberto: "Recentes"/pastas somem pra dar
           a coluna INTEIRA ao editor (pedido do usuário) — o gesto é
           temporário e reversível (Cancelar/Salvar devolvem a tela normal),
           então esconder aqui não perde estado nenhum da lista. -->
      <div v-if="!modoPersonalizacao" class="chat-sidebar__secoes">
        <!-- ⚠️ Na imersiva os agentes vivem no Ctrl+K, junto com as conversas e os
           módulos: a coluna já segura os insights e o histórico, e a troca de
           agente é ação de teclado, não de navegação constante.
           A prop `agentes` NÃO sai com a seção — ela ainda nomeia o agente de
           cada linha do histórico (`chat-sidebar-recents` indexa persona →
           rótulo por ela) e alimenta o agrupamento da rota cheia. -->
        <ChatSidebarGroup
          v-if="temAgentes && !comoNavegacao"
          :titulo="texto.secaoAgentes"
          icon-name="ai-sparkle"
          :aberto="secoesAbertas.agentes"
          @toggle="alternarSecao(secoes.agentes)"
        >
          <button
            v-for="agente in itensAgentes"
            :key="agente.id"
            type="button"
            class="ia-sidebar-row"
            :class="{ 'ia-sidebar-row--ativo': agente.ativo }"
            @click="$emit('select-agent', agente.id)"
          >
            <icon-svg
              :name="agente.iconName"
              :size="tamanhoIcone"
              :color="agente.corIcone"
              class="flex-shrink-0"
            />
            <span class="text-truncate">{{ agente.label }}</span>
          </button>
        </ChatSidebarGroup>

        <ChatSidebarGroup
          ref="secaoRecentes"
          :titulo="texto.secaoRecentes"
          :icon-name="iconeDaSecaoRecentes"
          :contador="contadorDeRecentes"
          :aberto="secoesAbertas.recentes"
          @toggle="alternarSecao(secoes.recentes)"
        >
          <!-- Linhas-fantasma com a anatomia da conversa (`chat-sidebar-esqueleto`):
             na navegação, título e hora na mesma caixa de 34px da linha real. -->
          <div v-if="mostraCarregando" class="chat-sidebar__esqueleto">
            <ChatSidebarEsqueleto
              :quantidade="LINHAS_ESQUELETO_INICIAL"
              :navegacao="comoNavegacao"
            />
          </div>

          <div v-else-if="mostraErro" class="chat-sidebar__aviso">
            <div class="ia-sidebar-aux mb-2">{{ texto.erro }}</div>
            <BaseButton underlined min-width="80px" @click="$emit('retry')">
              {{ texto.tentarDeNovo }}
            </BaseButton>
          </div>

          <div
            v-else-if="mostraVazio"
            class="ia-sidebar-aux chat-sidebar__aviso"
          >
            {{ texto.vazio }}
          </div>

          <div
            v-else-if="buscaSemResultado"
            class="ia-sidebar-aux chat-sidebar__aviso"
          >
            {{ texto.buscaSemResultado }}
          </div>

          <ChatSidebarRecents
            v-else
            :conversas="conversasFiltradas"
            :pastas="pastas"
            :agentes="agentes"
            :selected-id="selectedId"
            :estilo-limpo="comoNavegacao"
            :permitir-selecao-lote="comoNavegacao"
            :selected-ids="selectedIds"
            @select="onSelect"
            @rename="onRename"
            @request-delete="confirmDelete"
            @move="moverConversa"
            @toggle-select="onToggleSelect"
          />

          <!-- Rodapé da rolagem infinita. Três linhas-fantasma com a caixa da
             conversa, e não um spinner: a lista continua onde parou e o que se
             vê são as PRÓXIMAS linhas chegando. Spinner centralizado aqui leria
             como "a lista inteira está recarregando".
             ★ É o FIM rolável do conteúdo: a pessoa pode rolar até o fundo e
             vê-lo lá. A página entra no lugar dele no mesmo tick (o `v-if`
             cai junto com o append — `view-chat.carregarMaisConversas`) e a
             lista só cresce para baixo. A <transition> é só de ENTRADA
             (opacidade); a saída é instantânea, para o elemento que sai não
             ocupar nem soltar espaço. -->
          <transition name="chat-sidebar-fantasma">
            <div v-if="mostraCargaDeMais" class="chat-sidebar__mais">
              <ChatSidebarEsqueleto :quantidade="LINHAS_FANTASMA" navegacao />
            </div>
          </transition>
        </ChatSidebarGroup>

        <!-- Terceira seção só na rota cheia: na imersiva a lateral é o trilho da
           TELA, e agrupar por pasta é organização de arquivo — cabe onde há
           espaço e abas, não na coluna que também segura os insights. -->
        <ChatSidebarGroup
          v-if="!comoNavegacao"
          :titulo="texto.secaoAgrupar"
          icon-name="category"
          :aberto="secoesAbertas.agrupar"
          @toggle="alternarSecao(secoes.agrupar)"
        >
          <ChatSidebarGroups
            :conversas="conversasFiltradas"
            :pastas="pastas"
            :agentes="agentes"
            :contexto-listagem="eixoDosRecarregamentos"
            :selected-id="selectedId"
            :carregando="carregando"
            :erro="erro"
            @select="onSelect"
            @rename="onRename"
            @request-delete="confirmDelete"
            @move="moverConversa"
            @retry="$emit('retry')"
          />
        </ChatSidebarGroup>
      </div>
    </div>

    <!-- ★ Rodapé Cancelar/Restaurar/Salvar do editor de personalização —
         IRMÃO do `__corpo`, nunca dentro dele (mesma disciplina da paleta
         acima da barra): fora da área que rola, fica numa posição ESTÁVEL,
         colado onde o "Meus créditos" volta quando o editor fecha. -->
    <!-- Rodapé do editor × "Meus créditos": os dois no MESMO lugar, um de
         cada vez. A troca acontece com o corpo inteiro esmaecido (ver
         `trocarModoComFade`), então não precisa de transição própria. -->
    <PersonalizacaoFooterActions
      v-if="modoPersonalizacao"
      :dirty="personalizacaoDirty"
      :loading-save="salvandoPersonalizacao"
      sem-aviso
      embutido
      cancelar-sem-edicao
      @cancel="aoCancelarPersonalizacao"
      @save="aoSalvarPersonalizacao"
      @reset="aoRestaurarPersonalizacaoPadrao"
    />
    <!-- ★ Rodapé: IRMÃO do `__corpo`, nunca dentro dele. Ali é o que ROLA — a lista de
         conversas passaria por baixo do saldo e o número sairia da tela justamente na
         conversa longa, que é quando o consultor mais precisa dele.
         Só na configuração de NAVEGAÇÃO: na de HISTÓRICO a lateral é uma lista ao lado
         do menu da plataforma, que já leva à Central de IA por conta própria — repetir
         o atalho ali seria duas portas para a mesma sala.
         `recolhida` desce como estado porque a régua de 64px não comporta o número; o
         componente troca a linha pelo ícone e mantém o nome acessível completo.
         ⚠️ Some com o editor de personalização aberto (decisão do usuário): o
         saldo não tem relação nenhuma com "organizar módulos", e ficava
         disputando espaço visual com o rodapé Cancelar/Restaurar/Salvar logo
         acima — o consultor sempre pode fechar o editor pra vê-lo de novo. -->
    <SaldoCreditosRodape
      v-else-if="comoNavegacao"
      :recolhida="recolhida"
    />

    <!-- Gesto de recolher NA DIVISA, metade para fora da borda direita —
         espelha o botão da navbar principal (`sidebar-shell.vue`). O gesto ali
         é "mover a parede", não "clicar num ícone da lista", e era como ele
         lia enquanto morava junto da lupa e do `+`. -->
    <button
      v-if="comoNavegacao"
      type="button"
      class="chat-sidebar__retrair"
      :title="tituloRecolher"
      :aria-label="tituloRecolher"
      @click="alternarRecolhida"
    >
      <icon-svg
        :name="iconeRecolher"
        :size="TAMANHO_ICONE_RETRAIR"
        color="var(--black)"
      />
    </button>

    <!-- Seleção em lote das "Recentes" (ctrl+click, só na navegação
         imersiva) — `position: fixed` no próprio componente, a posição no
         DOM não importa pro visual. Mesmo uso de
         `curadoria/triagem-lista.vue`. -->
    <AcoesLote
      v-if="selectedIds.length"
      :value="selectedIds"
      :acoes="acoesLote"
      tipo="conversas"
      tipo-singular="conversa"
      select-text="selecionadas"
      select-text-singular="selecionada"
      @input="limparSelecaoLote"
    />
  </aside>
</template>

<style lang="sass" scoped>
// ── Geometria do trilho ─────────────────────────────────────────────────────
// Duas larguras porque são dois papéis. Na configuração de HISTÓRICO
// (`/aawz-intelligence/chat-ia` e o painel do Hub) a coluna é a lista de
// conversas ao lado do menu de módulos da plataforma. Na de NAVEGAÇÃO (a visão
// imersiva `/ia`) ela é a única navegação da tela e ainda carrega os cards de
// insight — a 288px o par compacto ficava com ~122px por card e o meta da
// conversa comia o título.
$largura-historico: 288px
// ★ 30% da janela, com piso e teto (28/08). Os cards de insight passaram a
// andar lado a lado — CRM com Financeiro, Comissões com Tarefas, os compactos
// em trio — e isso pede largura que 400px fixos não davam.
//
// O piso é a largura de antes: em tela pequena a lateral não pode encolher
// abaixo do que já funcionava. O teto existe porque 30% de um monitor de 2560px
// são 768px, e aí a lateral passa a competir com o chat em vez de servi-lo.
// 30% → 27% e teto 560 → 520 (17/09/2026, pedido do Herik: "um pouquinho
// menor"). O piso de 400 fica: é o que faz os 9 tiles da faixa "Adicionar
// módulo" caberem numa fileira (9×36 + 8×6 = 372 dos 376 úteis).
$largura-navegacao: clamp(400px, 27%, 520px)
$largura-recolhida: 64px

// Respiro lateral da casca. A navegação ganha 4px a mais de cada lado: a mesma
// gutter de 8px numa coluna de 400px deixa o conteúdo colado na parede.
$respiro-historico: 8px
$respiro-navegacao: 12px
$respiro-recolhida: 4px

// Movimento do recolher/expandir. Curto: é navegação, não celebração.
$duracao-do-recolher: 180ms
$curva-do-recolher: cubic-bezier(0.2, 0, 0, 1)

// Distância do "+" à borda direita da coluna. Ela é medida contra o botão da
// DIVISA (26px, metade para fora ⇒ 13px por dentro), não contra a parede — por
// isso o recuo da barra desconta o respiro da casca em vez de ser um valor
// fixo, e continua valendo se a gutter mudar de novo.
$folga-da-divisa: 26px

// ── Ritmo vertical da configuração de NAVEGAÇÃO ─────────────────────────────
// A paleta ficou plana (superfície #F5F5F5 contra conteúdo #FCFCFC, hairline
// #E0E0E0): separação por COR quase não existe mais, então quem separa é o
// ESPAÇO. Na coluna de histórico, que é densa de propósito, nada disto vale.
//
// ⚠️ Orçamento, medido antes de escolher os valores: a coluna ROLA (`__corpo`).
// Numa janela de 900px sobram ~796px para o corpo e o PIOR caso (9 cards de
// insight + 5 conversas) já ocupava ~778px — havia só **18px** de folga, não os
// ~70px que a largura nova sugere. Por isso o ar aqui é comprado, não gasto: as
// divisórias entre os cards saem (−7px) e pagam parte do padding maior, e o
// respiro que sobra vai para onde o olho reclama. O pacote fecha em ~+48px, o
// que empurra para fora da dobra apenas o "Ver todas" — as 5 conversas, o
// cabeçalho da seção e os 9 cards continuam visíveis sem rolar.
// ★ 22px (era 16) desde 28/08. O orçamento acima foi refeito: a lateral ficou
// mais larga (`$largura-navegacao`) e o hero passou a resolver valor e legenda
// numa linha só, o que devolveu altura ao pacote. Os 6px extras de cada lado
// dão 12px a mais de vale entre um card e o seguinte — que é o que faltava para
// a divisória ler como pausa, e não como aperto.
$respiro-bloco-insight: 22px
$respiro-fim-do-bloco: 8px

// ★ Piso de altura do bloco de situação (28/08/2026) — sem ele, trocar de
// conversa na lista "Recentes" fazia o PRÓPRIO "Recentes" pular de posição:
// o painel de insights do escritório (~9 cards, estável entre cliques) e o
// `IaClientePainel` (varia de cliente para cliente — 4 dos 6 blocos mostram
// de 0 a 3 itens cada) raramente têm a mesma altura. Aplicado só como PISO
// (`min-height`, nunca `height` fixo com scroll interno): a regra logo
// abaixo (`.chat-sidebar__secoes { overflow-y: visible }`) já deixa
// explícito que aqui só o `__corpo` rola — outro scroll aninhado reabriria
// o mesmo defeito ("prendia o topo, metade do conteúdo ficava
// inalcançável") que aquela regra existe pra evitar. Cliente com MUITO
// dado (as 4 listas cheias) ainda empurra "Recentes" pra baixo — só não há
// mais vaivém entre estados mais leves, que é o caso comum.
//
// Estimado a partir da geometria dos cards acima (hero ~117 + escala ~98 +
// 2× serie ~102 + progresso ~102 + compactos ~150-160), não medido ao vivo
// no navegador — calibrar visualmente e ajustar este número se destoar.
$altura-minima-painel-superior: 660px

// Sem borda e sem raio: a sidebar e a área de conteúdo formam UMA peça
// (AC-1.1). O que as separa é a superfície quente, não um card.
.chat-sidebar
  display: flex
  flex-direction: column
  flex-shrink: 0
  width: $largura-historico
  padding: 12px $respiro-historico
  background: var(--sidebar-warm-bg)
  // Acompanha a altura do chat em vez de crescer pelo conteúdo: `min-height: 0`
  // é o que permite o filho rolável encolher dentro do flex.
  align-self: stretch
  min-height: 0
  // ★ SEM `overflow` aqui, e `position: relative` — as duas coisas existem por
  // causa do botão da divisa. `overflow-y: auto` na casca (como era até 26/08)
  // força `overflow-x` a `auto` junto e recortaria o `right: -13px` dele; e sem
  // `relative` o `absolute` ancoraria no ancestral posicionado errado. O scroll
  // desceu um nível, para `__corpo`.
  // `!important` porque a raiz é um <aside> e a nav-bar tem a regra global
  // `aside { position: fixed !important }` — ver o comentário no <template>.
  position: relative !important
  // Arredondado só na esquerda: a direita encosta no conteúdo do chat.
  border-radius: var(--radius-card) 0 0 var(--radius-card)

  // Recolher/expandir animado. ⚠️ `flex-basis` JUNTO de `width`: quem manda no
  // tamanho de um item flex é a base — animar só a largura deixa o movimento
  // pela metade (é a mesma razão pela qual o `--recolhida` declara as duas).
  // O padding entra porque ele também muda entre as duas configurações.
  transition: width $duracao-do-recolher $curva-do-recolher, flex-basis $duracao-do-recolher $curva-do-recolher, padding $duracao-do-recolher $curva-do-recolher

  @media (prefers-reduced-motion: reduce)
    transition: none

// O que rola. `min-height: 0` de novo: sem ele o filho de um flex não encolhe
// abaixo do conteúdo, e o scroll voltaria para a página.
.chat-sidebar__corpo
  flex: 1
  min-height: 0
  overflow-y: auto
  // Ancoragem de rolagem do browser LIGADA (é o default, declarado para
  // ninguém desligar por engano): a rolagem infinita insere linhas ABAIXO do
  // que se lê e o `scrollTop` tem que ficar onde a pessoa deixou.
  overflow-anchor: auto

  // ⚠️ A barra de rolagem GLOBAL (`design/index.sass`) pinta o trilho de
  // `--neutral-50` — branco. Ele desaparece no fundo branco da plataforma, que
  // é onde a regra nasceu, mas sobre a superfície quente daqui vira uma faixa
  // clara colada na divisa. Trilho TRANSPARENTE em vez do token quente: assim
  // ele acompanha a superfície sozinho, inclusive se ela mudar de tom.
  &::-webkit-scrollbar-track
    background: transparent

  // O polegar sai do #ccc frio global para a família quente da lateral.
  &::-webkit-scrollbar-thumb
    background: var(--sidebar-warm-border)
    box-shadow: none

    &:hover
      background: var(--neutral-300)

  // Firefox não tem os pseudo-elementos acima; `scrollbar-color` é o
  // equivalente (polegar, trilho).
  scrollbar-width: thin
  scrollbar-color: var(--sidebar-warm-border) transparent

// Botão da divisa — receita da navbar principal (`sidebar-shell.vue`), com uma
// diferença: `top` alinhado ao centro da barra do topo daqui (12px de padding +
// metade dos 32px da linha), e não os 78px de lá, que ficam abaixo de um bloco
// de marca de 74px.
//
// `right: -13px` em botão de 26px = exatamente metade para fora. Depende da
// casca não ter `overflow` (ver acima) e ser `position: relative`.
.chat-sidebar__retrair
  position: absolute
  top: 15px
  right: -13px
  z-index: 20
  display: flex
  align-items: center
  justify-content: center
  width: 26px
  height: 26px
  border-radius: 999px
  // Pastilha clara com ícone escuro: ela vive sobre a divisa lateral/conteúdo,
  // e o anel cinza é o que a separa das duas superfícies.
  border: 1px solid var(--neutral-200)
  background: color-mix(in srgb, var(--white) 90%, transparent)
  box-shadow: 0 2px 8px color-mix(in srgb, var(--black) 16%, transparent)
  cursor: pointer
  transition: background var(--motion-duration-fast) var(--motion-easing-standard)

  // O original da navbar não tem nenhum dos dois — é lacuna de lá, não padrão.
  &:hover
    background: var(--white)

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px


.chat-sidebar__nova
  font-weight: 600
  color: var(--neutral-900)

// Bloco de módulos: respiro em cima e embaixo, mais uma hairline abaixo, para
// ele ler como uma seção à parte — e não como continuação de "Nova conversa".
// Bloco de insights: a borda de cada card já separa as linhas entre si; o
// respiro aqui é só o de entrada e saída do bloco.
.chat-sidebar__insights
  display: flex
  flex-direction: column
  padding: 0 8px

// O respiro e a divisória das linhas COMPACTAS moram aqui, não no card: num
// par seriam dois de cada. Vale também para a linha compacta de um item só —
// sem isto ela fica colada na de cima e sem divisória embaixo.
// ⚠️ Na configuração de NAVEGAÇÃO a divisória é retirada (lá quem separa é o
// ar, e a régua é uma só, no fim do bloco) — o padding daqui continua valendo.
// Acompanha o `$respiro-bloco-insight` dos blocos grandes: aqui o padding mora
// na LINHA (vários cards a dividem), mas o vale que o olho vê tem de ser o
// mesmo — senão a fileira dos compactos fica visivelmente mais apertada que o
// resto da coluna.
.chat-sidebar__insight-linha--compacta
  padding: 20px 0
  border-bottom: 1px solid var(--sidebar-warm-border)

// 16px (e não os 12px de quando a coluna tinha 288px): com a coluna larga cada
// card do par passa de ~122px para ~172px ou mais, e o vale entre eles precisa
// crescer junto — senão os dois números leem como um bloco só.
//
// `--colunas` vem inline, da linha (ver o `v-for` no template). O fallback 2
// existe porque uma linha sem a variável renderizaria `repeat(, 1fr)`, que é
// declaração inválida e derruba a grade inteira para o fluxo normal.
// ★ A grade só entra com DOIS ou mais (`itens.length > 1`, no template). Linha
// de um item fica bloco comum e ocupa a largura toda sozinha — é o que faz o
// vizinho perdido por falta de flag, e a sobra do trio (o Partnership), não
// ficarem num terço com dois terços de buraco.
.chat-sidebar__insight-linha--grade
  display: grid
  grid-template-columns: repeat(var(--colunas, 2), 1fr)
  gap: 16px

// ── Troca entre o painel do escritório e o do cliente ───────────────────────
// ⚠️ Fica AQUI (`scoped`): um `fade` de outro componente não alcança esta
// árvore. A receita é a do DS de propósito — duas durações diferentes fariam
// a `/ia` ter dois ritmos de transição. (O `chat-header`, que tinha um `fade`
// idêntico, saiu do repo em 11/09/2026 junto com a pílula de fase.)
.fade-enter-active,
.fade-leave-active
  transition: opacity var(--motion-duration-default) var(--motion-easing-standard)

.fade-enter,
.fade-leave-to
  opacity: 0

// Entrar/sair do editor de personalização: UMA animação — tudo abaixo da
// barra do topo (faixa, corpo, rodapé/saldo) esmaece junto, o modo troca com
// o corpo invisível e ele volta já montado no modo novo. Uma transição por
// peça (faixa descendo, rodapé subindo, saldo saindo, canvas trocando)
// virava pisca-pisca (screencast do usuário, 16/09/2026). Só `opacity`,
// nunca `display`: os elementos continuam no lugar, sem reflow no meio.
// ⚠️ A duração espelha `DURACAO_DA_TROCA_DE_MODO_MS` (`chat-sidebar-personalizacao.js`) — o timer é
// quem troca o modo, e tem que esperar o fade terminar.
$duracao-da-troca-de-modo: 160ms

.chat-sidebar > :not(.chat-sidebar__barra):not(.chat-sidebar__retrair)
  transition: opacity $duracao-da-troca-de-modo var(--motion-easing-standard)

.chat-sidebar--trocando-modo > :not(.chat-sidebar__barra):not(.chat-sidebar__retrair)
  opacity: 0
  pointer-events: none

// Com o corpo esmaecido, o `fade` interno do painel (a `<transition
// mode="out-in">` de insights × cliente × personalização) é neutralizado:
// o Vue não vê transição de CSS e troca seco — a troca acontece invisível,
// e só o corpo anima. Ver o comentário no template.
.chat-sidebar--trocando-modo .fade-enter-active,
.chat-sidebar--trocando-modo .fade-leave-active
  transition: none

// A troca de painel é conteúdo aparecendo e sumindo — exatamente o que a
// preferência cobre. Sem transição ela continua acontecendo, só que instantânea.
@media (prefers-reduced-motion: reduce)
  .fade-enter-active,
  .fade-leave-active,
  .chat-sidebar > :not(.chat-sidebar__barra):not(.chat-sidebar__retrair)
    transition: none

// ── Régua recolhida ─────────────────────────────────────────────────────────
// ★ Os botões são `.chat-sidebar__botao-topo`, os MESMOS de buscar e nova
// conversa logo acima — caixa, raio, hover e foco vêm de lá. Uma receita
// própria aqui já divergiu uma vez (alvo maior e sem raio, ao lado dos
// originais na mesma coluna); a régua só cuida da coluna e da divisa.
.chat-sidebar__atalhos
  display: flex
  flex-direction: column
  align-items: center
  gap: 4px
  // Sem padding no topo: a divisa de entrada já dá o respiro (o `margin` dela),
  // e os dois juntos afastavam demais a régua do botão de personalizar.
  padding: 0 0 8px

// O histórico não é módulo: a régua acima leva para FORA da IA, ele volta para
// dentro dela. A divisa diz isso sem precisar de rótulo, que não caberia.
.chat-sidebar__atalhos-divisa
  width: 24px
  height: 1px
  margin: 6px 0
  background: var(--sidebar-warm-border)

.chat-sidebar__nav
  display: flex
  flex-direction: column
  margin: 12px 0
  padding-bottom: 12px
  border-bottom: 1px solid var(--sidebar-warm-border, rgba(0, 0, 0, 0.06))

// Marca do escritório, à esquerda da barra do topo.
.chat-sidebar__marca
  display: flex
  align-items: center
  min-width: 0

// Menor que os 38px da navbar principal: a marca abre uma coluna, não é o
// cabeçalho da plataforma. Quem manda é a ALTURA — o teto de largura só existe
// para a logo horizontal não empurrar a lupa e o "+", e a 400px ele podia
// subir (em 150px uma logo bem deitada era reduzida abaixo dos 22px de altura).
.chat-sidebar__marca-img
  max-height: 22px
  max-width: 200px
  object-fit: contain

.chat-sidebar__marca-inicial
  color: var(--text-on-primary, var(--white))
  display: inline-flex
  align-items: center
  justify-content: center
  width: 24px
  height: 24px
  border-radius: var(--radius-sm, 6px)
  background: var(--primary)

// Barra do topo: marca à esquerda, ações à direita — daí o `space-between`.
.chat-sidebar__barra
  display: flex
  align-items: center
  justify-content: space-between
  gap: 8px
  min-height: 32px
  // O recuo da DIREITA existe por causa do botão da divisa, que invade esta
  // faixa: ele começa 13px antes da borda da coluna, e com os 4px de antes o
  // "+" encostava nele (chegava a sobrepor 1px).
  // ⚠️ A conta é contra a BORDA da coluna, e a casca já recua o conteúdo — daí
  // o desconto: com a gutter de 12px da navegação, um `18px` fixo teria
  // afastado o "+" mais 4px da divisa e desfeito o par lupa/"+".
  // Esta barra só existe com `comoNavegacao`, então o respiro é o de lá.
  padding: 0 ($folga-da-divisa - $respiro-navegacao) 4px 4px

// 6px, a régua do protótipo: os dois lêem como um par, não como itens soltos
// de uma barra.
.chat-sidebar__acoes-topo
  display: flex
  align-items: center
  gap: 6px

// A pastilha das três ações do topo. Nasceu como `.chat-sidebar__recolher` e
// virou receita compartilhada quando busca e nova conversa subiram para cá —
// as medidas (28×28, raio 8) são as do protótipo.
.chat-sidebar__botao-topo
  display: inline-flex
  align-items: center
  justify-content: center
  width: 28px
  height: 28px
  border-radius: var(--radius-sm, 8px)
  flex-shrink: 0
  background: transparent

  &:hover
    background: var(--sidebar-warm-hover)

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

// Recolhida: só os ícones. O conteúdo textual some por `display: none` em vez
// de `width: 0`, senão o flex continua reservando a largura do texto.
//
// ★ Classe DOBRADA (0,2,0) de propósito: recolher só existe na configuração de
// navegação, então este seletor vem sempre junto com `--navegacao`. Em (0,1,0)
// os dois empatariam e venceria o último do arquivo — que é o `--navegacao`,
// e o trilho de 64px nasceria com 400.
.chat-sidebar.chat-sidebar--recolhida
  width: $largura-recolhida
  // ⚠️ `flex-basis` OBRIGATÓRIO junto do `width`. A `--navegacao` declara os
  // dois, e as duas classes convivem quando a lateral está recolhida — só o
  // `width` sendo sobrescrito, a base continuava em `clamp(400px, …)`, e como o
  // item tem `flex-shrink: 0` ele nunca encolhia: o botão de recolher parecia
  // não fazer nada. Quem manda no tamanho de um item flex é a BASE, não o
  // `width`.
  flex-basis: $largura-recolhida
  padding-left: $respiro-recolhida
  padding-right: $respiro-recolhida

  .ia-sidebar-row
    justify-content: center

  .ia-sidebar-row span
    display: none

  // Recolhida sobram só buscar e nova conversa — a marca some (logo horizontal
  // não cabe em 64px, e cortada lê pior que ausente) e o recolher mora na
  // divisa. Centralizadas no trilho: encostadas à esquerda pareciam deslocadas.
  .chat-sidebar__barra
    flex-direction: column
    justify-content: center
    gap: 6px
    padding-left: 0
    padding-right: 0

  .chat-sidebar__acoes-topo
    flex-direction: column

  // ★ Só no trilho, o "+" vem ANTES da lupa (pedido do usuário, 11/09/2026):
  // recolhida, a coluna é atalho de ação, e "começar de novo" é a ação que
  // mais se usa ali. Por `order`, não por markup: o DOM (e a ordem de Tab)
  // continua lupa → "+", igual à expandida, e a regra é inofensiva quando a
  // lupa não renderiza (`mostraBuscaGlobal` falso) — um item só não tem o que
  // reordenar.
  .chat-sidebar__botao-topo--nova
    order: -1

  // Busca e listas não cabem em 64px e não têm forma de ícone. Some tudo:
  // recolhida, a lateral é um trilho de atalhos, não um histórico.
  .ia-sidebar-busca,
  .chat-sidebar__secoes
    display: none

  .chat-sidebar__nav
    margin-bottom: 0
    border-bottom: none

// A receita do campo de busca (`.ia-sidebar-busca`) e a do progresso
// (`.ia-sidebar-progresso`) moram em `design/_ia-sidebar.sass`, junto com o
// resto da superfície quente: é lá que os tamanhos de 11/12/13px têm um dono
// só, como exige a exceção que os autoriza.
.chat-sidebar__secoes
  flex: 1
  overflow-y: auto

// A configuração de NAVEGAÇÃO — a lateral da visão imersiva `/ia`.
// ★ [feedback do usuário, 02/09] `scrollbar-gutter: stable` DE VOLTA. Tinha
// sido removido em 31/08 (a calha ~11px ficava visível permanentemente sem
// função aparente na maior parte do tempo) — mas abrir "Recentes" volta a
// mudar o total de conteúdo do `__corpo` com frequência, e cada vez que a
// barra aparece/some o texto e as barras de progresso dos cards acima
// deslocam alguns pixels pra esquerda. O deslocamento incomodou mais que a
// calha fixa incomodava — o comentário anterior já previa essa possível
// volta. A barra em si é fina (7px, `design/index.sass` + `scrollbar-width:
// thin` abaixo), então a calha reservada fica pequena, não uma barra "gorda".
.chat-sidebar--navegacao:not(.chat-sidebar--recolhida) .chat-sidebar__corpo
  scrollbar-gutter: stable
  // ⚠️ Ao EXPANDIR, a classe cai no primeiro quadro e o conteúdo monta dentro
  // de uma coluna que ainda tem 64px — sem isto ele nasce espremido e vai se
  // desentortando durante a animação, que é o mesmo defeito do recolher visto
  // do outro lado. Segurando a largura final e recortando, ele entra pronto e
  // é REVELADO pela coluna que abre.
  overflow-x: hidden

// ⚠️ [15/09/2026] SÓ enquanto a largura anima (`--animando`): como regra
// permanente, o `min-width` era a largura da coluna SEM descontar a calha da
// barra de rolagem (`scrollbar-gutter: stable`, ~10px) — com a lateral no piso
// de 400px (janela de ~1280px, DevTools aberto) o conteúdo nascia 10px mais
// largo que o corpo e o `overflow-x: hidden` cortava a borda direita do
// painel do cliente (contadores, "0.8", a hora dos Recentes). Fora da
// animação a coluna já tem a largura final e o conteúdo se ajusta a ela.
.chat-sidebar--navegacao.chat-sidebar--animando:not(.chat-sidebar--recolhida) .chat-sidebar__corpo > *
  min-width: calc(#{$largura-navegacao} - #{$respiro-navegacao * 2})

.chat-sidebar--navegacao
  width: $largura-navegacao
  // ★ `flex-basis` junto com `width`: a lateral é item de um flex e, com
  // `flex-shrink: 0` mas base `auto`, alguns motores resolvem a porcentagem
  // contra o conteúdo em vez do container. Declarar a base fecha isso.
  flex-basis: $largura-navegacao
  padding-left: $respiro-navegacao
  padding-right: $respiro-navegacao

  // ── Ritmo vertical ────────────────────────────────────────────────────────
  // O bloco de situação é UMA peça, fechada por UMA régua. Entre um insight e o
  // seguinte quem separa é o ar: com uma hairline no meio de cada par, sete
  // cards liam como linhas de tabela, não como blocos — e era daí que vinha o
  // "estão colados", mesmo com 29px entre os textos.
  // Sem `padding-bottom`: os 16px de baixo do último card já afastam a régua do
  // conteúdo. Abaixo dela vem menos ar do que acima (8 contra 16) de propósito
  // — assim ela lê como fechamento do bloco de situação, e não como uma linha
  // solta entre duas seções.
  .chat-sidebar__insights
    border-bottom: 1px solid var(--sidebar-warm-border)
    margin-bottom: $respiro-fim-do-bloco
    // Ver o comentário de `$altura-minima-painel-superior` acima: piso, não
    // altura fixa — cliente com pouco dado sobe até aqui, cliente com muito
    // segue crescendo além disso normalmente.
    min-height: $altura-minima-painel-superior

  .chat-sidebar__insight-linha,
  .chat-sidebar__insight-linha--compacta
    border-bottom: none

  // `:not(--compacto)` é obrigatório: no card compacto o padding vertical mora
  // na LINHA (dois cards dividem uma), e o card zera o próprio — dar padding a
  // ele aqui somaria os dois.
  // 16px em cima e embaixo ⇒ 32px de ar limpo entre dois blocos, contra os 29px
  // que antes vinham partidos ao meio por uma régua.
  //
  // ⚠️ `:not(--cliente)` no CONTÊINER: o painel do cliente reusa
  // `__insights` (mesma posição na coluna) mas embute o `IaInsightCard` DENTRO
  // de uma das suas seções, que já desenha o próprio ritmo. Sem este recorte, o
  // respiro de bloco caía sobre um card que é peça de outro bloco — e vencia a
  // regra do componente por especificidade, que foi como o vão apareceu.
  .chat-sidebar__insights:not(.chat-sidebar__insights--cliente) ::v-deep .ia-insight:not(.ia-insight--compacto)
    padding-top: $respiro-bloco-insight
    padding-bottom: $respiro-bloco-insight

  // Aqui a lateral INTEIRA rola, não só as listas. Com marca, insights e
  // histórico empilhados, um scroll interno só nas seções prendia o topo e
  // deixava metade do conteúdo inalcançável — quem rola é o `__corpo`.
  .chat-sidebar__secoes
    flex: 0 0 auto
    overflow-y: visible

// Editor de personalização — ocupa a coluna INTEIRA (pedido do usuário:
// "Recentes" some enquanto o editor está aberto, ver `v-if` do
// `.chat-sidebar__secoes` no template), não mais o orçamento de
// `$altura-minima-painel-superior` do modo normal. A faixa "Adicionar
// módulo" fica ACIMA, fora do corpo (ver `.chat-sidebar__personalizacao-paleta`).
.chat-sidebar__personalizacao
  display: flex
  flex-direction: column
  gap: 10px

// ⚠️ Reseta o que `.chat-sidebar--navegacao .chat-sidebar__insights` (a
// regra do ritmo vertical, acima) empresta pela OUTRA classe do mesmo
// elemento (`class="chat-sidebar__insights chat-sidebar__personalizacao"`):
// aquela regra fecha o painel NORMAL com uma hairline + respiro por baixo e
// um piso de 660px — aqui sobrava como uma divisória solta entre o canvas e
// a "ADICIONAR MÓDULO" fixa logo abaixo (achado do usuário ao vivo).
// Especificidade CASADA de propósito: a regra de lá é `.chat-sidebar--navegacao
// .chat-sidebar__insights` (duas classes); um `.chat-sidebar__personalizacao`
// solto (uma classe) PERDIA para ela e o reset nunca chegava a valer — foi o
// que aconteceu na primeira versão deste bloco.
.chat-sidebar--navegacao .chat-sidebar__personalizacao
  border-bottom: none
  margin-bottom: 0
  min-height: 0

// Com o editor aberto o corpo vira coluna flex e o canvas ESTICA até o fim
// dela — é o que deixa o "Nenhum card no seu painel" centrado no eixo Y do
// painel inteiro, não só dos 140px do alvo mínimo do drop (pedido do
// usuário). Cadeia inteira: corpo → wrapper do `v-show` → bloco → canvas,
// cada um `flex: 1` + `min-height: 0`, senão o primeiro sem isso quebra a
// corrente. Só neste modo: no normal o corpo é bloco e rola como sempre.
.chat-sidebar__corpo--personalizando
  display: flex
  flex-direction: column

.chat-sidebar__painel-superior--personalizando
  display: flex
  flex-direction: column
  flex: 1
  min-height: 0

  > .chat-sidebar__personalizacao
    flex: 1

  ::v-deep .personalizacao-canvas
    flex: 1

// "Adicionar módulo" — logo abaixo da barra do topo, IRMÃ do `__corpo` (ver
// o template): fora da área que rola. Faixa de fundo mais FORTE que a
// lateral, de borda a borda: a casca (`.chat-sidebar--navegacao`) recua o
// conteúdo em `$respiro-navegacao` de cada lado, e a margem negativa devolve
// esse recuo pra faixa encostar nas bordas da coluna — o padding interno
// repõe o respiro dos tiles (mesmo truque do rodapé do CRM).
.chat-sidebar__personalizacao-paleta
  flex-shrink: 0
  margin: 8px (-$respiro-navegacao) 8px
  // Simétrico (12/12), medido com o usuário: é o que deixa a fileira de
  // TILES com o mesmo ar embaixo que o título tem em cima. O estado sem
  // tile (texto centrado num slot de 36px) compensa a folga própria do
  // slot do lado dele — ver `&__vazio` em `personalizacao-paleta.vue`.
  padding: 12px $respiro-navegacao
  // MEIO degrau acima da superfície da lateral (#F4F4F4): `--neutral-200` e
  // até o `--sidebar-warm-hover` (#E7E7E7) ficaram fortes demais, fora do
  // padrão (achado do usuário, duas rodadas). Não há token entre os dois,
  // então mistura a borda da lateral na própria superfície — mesmo
  // `color-mix` sobre token que o repo já usa (`task-custom-fields-config`,
  // cards do CRM), nunca um hex novo.
  background: color-mix(in srgb, var(--sidebar-warm-border) 35%, var(--sidebar-warm-bg))
  // Hairline em cima E embaixo (decisão do usuário): a faixa é um bloco
  // fechado entre a barra do topo e o painel.
  border-top: 1px solid var(--sidebar-warm-border)
  border-bottom: 1px solid var(--sidebar-warm-border)

// `PersonalizacaoFooterActions` (`embutido`/`sem-aviso`) — IRMÃO do
// `__corpo`, fora da área que rola. Só a divisória na cor da lateral é daqui.
.chat-sidebar ::v-deep .personalizacaoFooter--embutido
  border-top-color: var(--sidebar-warm-border)

.chat-sidebar__aviso
  padding: 8px

// Texto à esquerda, botão de "Tentar de novo" à direita — não embaixo dele
// (ver o card "Indicadores indisponíveis"). Seletor COMPOSTO de propósito:
// `.chat-sidebar__insights` sozinho já define `flex-direction: column`
// (e, na navegação, `min-height: 660px` — ver `$altura-minima-painel-
// superior` acima) — mesma especificidade de UMA classe, então precisa de
// DUAS pra vencer sem depender da ordem no arquivo. `align-items:
// flex-start` (não `center`): o container pode ficar bem mais alto que o
// conteúdo (o piso de 660px), e centralizar jogaria o texto pro meio da
// caixa em vez de manter no topo.
.chat-sidebar__insights.chat-sidebar__aviso--linha
  flex-direction: row
  align-items: flex-start
  justify-content: space-between
  gap: 8px

.chat-sidebar__aviso-texto
  min-width: 0
  flex: 1 1 auto

.chat-sidebar__aviso-retry
  flex: 0 0 auto

.chat-sidebar__esqueleto
  padding: 4px 0

// Rodapé da rolagem infinita — três linhas na métrica da conversa
// (`chat-sidebar-esqueleto`), para a página que chega ocupar exatamente a
// caixa que elas deixam. Sem padding, altura ou margem próprios: o respiro é o
// das linhas, e a caixa é a delas.
//
// ★ Só a ENTRADA anima, e só opacidade — o elemento já ocupa a caixa inteira
// no primeiro quadro. Sem regra de `-leave-*` de propósito (14/09/2026): com
// colapso animado na saída, o fim rolável do `__corpo` andava para trás
// durante 200ms e o usuário via "chegar, voltar, chegar de novo". Sem
// transição de saída o Vue remove o nó no mesmo quadro em que a página
// entra — um evento só, e o `scrollTop` fica onde a pessoa deixou.
.chat-sidebar-fantasma-enter-active
  transition: opacity var(--motion-duration-default) var(--motion-easing-standard)

.chat-sidebar-fantasma-enter
  opacity: 0

@media (prefers-reduced-motion: reduce)
  .chat-sidebar-fantasma-enter-active
    transition: none

// Barra de rolagem HORIZONTAL calada enquanto a coluna abre ou fecha — ver
// `alternarRecolhida`. Só o eixo X: ao expandir, o `min-width` forçado (regra
// acima) deixa o conteúdo mais largo que a coluna ainda-estreita por um
// instante, e sem isto aquele excesso vazava como scroll horizontal.
//
// ⚠️ NÃO caiam no eixo Y aqui. Foi a primeira tentativa, e ela criava um
// flicker PRÓPRIO: a régua recolhida (ícones) pode legitimamente precisar de
// scroll vertical permanentemente, independente da animação — calando os dois
// eixos, esse scroll "aparece e some" junto com o timer, mesmo sem relação
// nenhuma com a largura. Deixando o Y de fora, ele nunca é tocado por esta
// regra: fica constante, sem pisca.
.chat-sidebar--animando .chat-sidebar__corpo
  overflow-x: hidden
</style>
